import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { actions as renderingActions } from 'builder/modules/rendering'
import { useUser } from 'builder/hooks/useUser'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as jobSearchSelectors } from 'builder/modules/jobSearch'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { navigate } from 'builder/modules/navigate'
import { useGetCareerProfile } from 'builder/views/AutoApply/hooks/useGetCareerProfile'
import { useAutoApplyUpsellModal } from 'builder/components/AutoApplyUpsellModal/useAutoApplyUpsellModal'
import { selectors as profileSelectors } from 'builder/modules/careerProfile'
import { StartCompleteScreenRoute } from '../constants'

export const useAutoApply = () => {
  const user = useUser()
  const dispatch = useDispatch()
  const { handleOpenModal: openUpsellModal } = useAutoApplyUpsellModal()
  const autoApplyBubbles = useTypedSelector(jobSearchSelectors.autoApplyBubbles)
  const isAutoApplyComplete = useTypedSelector(profileSelectors.isAutoApplyComplete)
  const { data: careerProfileData, isLoading: isCareerProfileDataLoading } = useGetCareerProfile()
  const { features } = useFeaturesConfig()

  const isAutoApplyFeatureEnabled = !!features?.autoApply
  const isResumeTailoringEnabled = careerProfileData?.autoTailoring
  const isUserOnAutoApplyPlan = isAutoApplyFeatureEnabled && !!user?.scopes?.autoApply
  const hasUserCompletedQuestionnaire = careerProfileData?.autoApplyDataCompleted

  const getAutoApplyCredit = useCallback(() => {
    if (typeof user?.scopes?.autoApply === 'object') {
      return user?.scopes?.autoApply
    }
    return { creditCount: 0, creditCountUsed: 0, creditCountMax: 0, autoApplyPlanType: null }
  }, [user])

  const getCreditCount = () => {
    if (user?.scopes?.autoApply === true) return '∞'

    const { creditCount, creditCountUsed } = getAutoApplyCredit()
    return `${creditCountUsed}/${creditCount}`
  }

  const isAutoApplyLimitReached = useCallback(
    (count = 1, triggerAction = true) => {
      if (autoApplyBubbles.running) return false
      const { creditCount, creditCountUsed, autoApplyPlanType } = getAutoApplyCredit()
      const usedCredits = creditCountUsed + count

      if (creditCount && usedCredits > creditCount) {
        if (triggerAction) {
          if (user?.scopes.kind === 'TR' && autoApplyPlanType === 'one_time') {
            dispatch(renderingActions.showRDUpsellModal())
          } else {
            dispatch(renderingActions.showRDNotEnoughCreditsModal())
          }
        }
        return true
      }
      return false
    },
    [autoApplyBubbles.running, dispatch, getAutoApplyCredit, user?.scopes?.kind],
  )

  const redirectToBillingPlans = useCallback(() => {
    if (!autoApplyBubbles.running && isAutoApplyFeatureEnabled && !isUserOnAutoApplyPlan) {
      openUpsellModal()
      return true
    }
  }, [autoApplyBubbles.running, isAutoApplyFeatureEnabled, isUserOnAutoApplyPlan, openUpsellModal])

  const redirectToAutoApplyQuestionnaire = useCallback(() => {
    if (!hasUserCompletedQuestionnaire) {
      navigate(`/auto-apply/${StartCompleteScreenRoute.START_SCREEN}`)
      return true
    }
    return false
  }, [hasUserCompletedQuestionnaire])

  return {
    getAutoApplyCredit,
    isAutoApplyLimitReached,
    redirectToBillingPlans,
    redirectToAutoApplyQuestionnaire,
    getCreditCount,
    isAutoApplyFeatureEnabled,
    isUserOnAutoApplyPlan,
    isResumeTailoringEnabled,
    hasUserCompletedQuestionnaire,
    isCareerProfileDataLoading,
    isAutoApplyComplete,
  }
}
